import type {
  MutationCacheNotifyEvent,
  Query,
  QueryCacheNotifyEvent,
  QueryClient,
} from '@tanstack/react-query';

import type PusherManager from '../../modules/session/services/pusher_manager';
import type { ChannelEventCallback } from '../../modules/session/services/pusher_manager';
import type { ConnectionStatus } from '../../modules/session/types/live_session';
import type { ChangeBatch, EntityChange } from './_generated';
import { recordRecentChanges } from './recentChanges';
import { CHANGE_EVENT, changeChannel, matches, type ScopeRef, type Watch } from './watches';

export const FLUSH_TRAILING_MS = 150;
export const FLUSH_MAX_WAIT_MS = 1000;
export const UNSUBSCRIBE_GRACE_MS = 30_000;
export const DEDUPE_CAPACITY = 1000;
export const RECONNECT_JITTER_MAX_MS = 15_000;
export const SUBSCRIPTION_RETRY_DELAY_MS = 2000;

const SUBSCRIPTION_SUCCEEDED_EVENT = 'pusher:subscription_succeeded';
const SUBSCRIPTION_ERROR_EVENT = 'pusher:subscription_error';
const FORBIDDEN_STATUS = 403;

export type ChangeRouterPusher = Pick<
  PusherManager,
  'subscribe' | 'unsubscribe' | 'bind' | 'unbind' | 'onConnectionStateChange'
>;

type Timer = ReturnType<typeof setTimeout>;

interface ChannelEntry {
  refCount: number;
  subscribed: boolean;
  graceTimer: Timer | null;
  retryTimer: Timer | null;
  retried: boolean;
  forbidden: boolean;
  onChange: ChannelEventCallback;
  onSubscribed: ChannelEventCallback;
  onError: ChannelEventCallback;
}

function watchesOf(query: Query): Watch[] {
  return query.meta?.watches ?? [];
}

export function isChangeBatch(data: unknown): data is ChangeBatch {
  return typeof data === 'object' && data !== null && Array.isArray((data as ChangeBatch).changes);
}

let activeRouter: ChangeRouter | null = null;

function registerActiveRouter(router: ChangeRouter | null): void {
  activeRouter = router;
}

export function deliverChanges(batch: ChangeBatch): void {
  activeRouter?.ingest(batch);
}

// pusher-js sets `status` only for its own HTTP authorizer; the custom authorizer reports the status as `error`.
function isForbidden(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) return false;
  const { status, error } = data as { status?: unknown; error?: unknown };
  return status === FORBIDDEN_STATUS || error === String(FORBIDDEN_STATUS);
}

export class ChangeRouter {
  private readonly channels = new Map<string, ChannelEntry>();
  private readonly queryChannels = new Map<string, Map<string, ScopeRef>>();
  private readonly seenChangeIds = new Set<string>();
  private readonly queued = new Set<string>();
  private readonly holds = new Map<string, number>();
  private readonly teardown: Array<() => void> = [];
  private trailingTimer: Timer | null = null;
  private maxWaitTimer: Timer | null = null;
  private reconnectTimer: Timer | null = null;
  private hasConnected = false;
  private droppedWhileWatching = false;
  private running = false;

  constructor(
    private readonly queryClient: QueryClient,
    private readonly pusher: ChangeRouterPusher,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    registerActiveRouter(this);
    const queryCache = this.queryClient.getQueryCache();
    this.teardown.push(
      queryCache.subscribe(this.handleQueryCacheEvent),
      this.queryClient.getMutationCache().subscribe(this.handleMutationCacheEvent),
      this.pusher.onConnectionStateChange(this.handleConnectionState),
    );
    queryCache.getAll().forEach((query) => this.syncQuery(query, false));
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    if (activeRouter === this) registerActiveRouter(null);
    this.teardown.splice(0).forEach((unsubscribe) => unsubscribe());
    [this.trailingTimer, this.maxWaitTimer, this.reconnectTimer].forEach((timer) => {
      if (timer !== null) clearTimeout(timer);
    });
    this.trailingTimer = null;
    this.maxWaitTimer = null;
    this.reconnectTimer = null;
    [...this.channels.keys()].forEach((channel) => this.dropChannel(channel));
    this.queryChannels.clear();
    this.seenChangeIds.clear();
    this.queued.clear();
    this.holds.clear();
    this.hasConnected = false;
    this.droppedWhileWatching = false;
  }

  hold(anchor: ScopeRef): () => void {
    const key = changeChannel(anchor);
    const count = this.holds.get(key) ?? 0;
    this.holds.set(key, count + 1);
    if (count === 0) this.requeueInFlight(key);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const remaining = (this.holds.get(key) ?? 1) - 1;
      if (remaining > 0) {
        this.holds.set(key, remaining);
      } else {
        this.holds.delete(key);
      }
      this.flushHeld();
    };
  }

  // A fetch that started before the hold may carry data older than the held edit; refetch it on release instead.
  private requeueInFlight(channel: string): void {
    this.queryClient
      .getQueryCache()
      .getAll()
      .forEach((query) => {
        if (query.state.fetchStatus !== 'fetching' || query.state.data === undefined) return;
        if (!watchesOf(query).some((watch) => changeChannel(watch.anchor) === channel)) return;
        this.queued.add(query.queryHash);
        void this.queryClient.cancelQueries({ queryKey: query.queryKey, exact: true });
      });
  }

  private handleQueryCacheEvent =(event: QueryCacheNotifyEvent): void => {
    switch (event.type) {
      case 'added':
      case 'observerAdded':
      case 'observerRemoved':
      case 'observerOptionsUpdated':
        this.syncQuery(event.query, false);
        break;
      case 'removed':
        this.syncQuery(event.query, true);
        break;
      default:
        break;
    }
  };

  private handleMutationCacheEvent = (event: MutationCacheNotifyEvent): void => {
    if (event.type === 'updated' || event.type === 'removed') {
      this.flushHeld();
    }
  };

  private handleConnectionState = (state: ConnectionStatus): void => {
    if (state !== 'connected') {
      if (this.hasConnected && this.channels.size > 0) {
        this.droppedWhileWatching = true;
      }
      return;
    }
    if (this.hasConnected && this.droppedWhileWatching) {
      this.scheduleReconnectRefresh();
    }
    this.channels.forEach((entry, channel) => {
      if (entry.refCount > 0 && !entry.subscribed && !entry.forbidden && entry.retryTimer === null) {
        this.resubscribe(channel, entry);
      }
    });
    this.hasConnected = true;
    this.droppedWhileWatching = false;
  };

  private syncQuery(query: Query, removed: boolean): void {
    const next = new Map<string, ScopeRef>();
    if (!removed && query.isActive()) {
      watchesOf(query).forEach((watch) => next.set(changeChannel(watch.anchor), watch.anchor));
    }
    const previous = this.queryChannels.get(query.queryHash) ?? new Map<string, ScopeRef>();

    next.forEach((_anchor, channel) => {
      if (!previous.has(channel)) this.acquireChannel(channel);
    });
    previous.forEach((_anchor, channel) => {
      if (!next.has(channel)) this.releaseChannel(channel);
    });

    if (next.size > 0) {
      this.queryChannels.set(query.queryHash, next);
    } else {
      this.queryChannels.delete(query.queryHash);
    }
  }

  private acquireChannel(channel: string): void {
    const existing = this.channels.get(channel);
    if (existing) {
      existing.refCount += 1;
      if (existing.graceTimer !== null) {
        clearTimeout(existing.graceTimer);
        existing.graceTimer = null;
      }
      if (!existing.subscribed && existing.retryTimer === null) this.resubscribe(channel, existing);
      return;
    }

    const entry: ChannelEntry = {
      refCount: 1,
      subscribed: false,
      graceTimer: null,
      retryTimer: null,
      retried: false,
      forbidden: false,
      onChange: (data) => {
        if (isChangeBatch(data)) this.ingest(data);
      },
      onSubscribed: () => this.handleSubscriptionSucceeded(channel),
      onError: (data) => this.handleSubscriptionError(channel, data),
    };
    this.channels.set(channel, entry);
    this.subscribeEntry(channel, entry);
  }

  private resubscribe(channel: string, entry: ChannelEntry): void {
    entry.retried = false;
    entry.forbidden = false;
    this.subscribeEntry(channel, entry);
  }

  private subscribeEntry(channel: string, entry: ChannelEntry): void {
    entry.subscribed = this.pusher.subscribe(channel) !== null;
    if (!entry.subscribed) return;
    this.pusher.bind(channel, CHANGE_EVENT, entry.onChange);
    this.pusher.bind(channel, SUBSCRIPTION_SUCCEEDED_EVENT, entry.onSubscribed);
    this.pusher.bind(channel, SUBSCRIPTION_ERROR_EVENT, entry.onError);
  }

  private unsubscribeEntry(channel: string, entry: ChannelEntry): void {
    if (!entry.subscribed) return;
    entry.subscribed = false;
    this.pusher.unbind(channel, CHANGE_EVENT, entry.onChange);
    this.pusher.unbind(channel, SUBSCRIPTION_SUCCEEDED_EVENT, entry.onSubscribed);
    this.pusher.unbind(channel, SUBSCRIPTION_ERROR_EVENT, entry.onError);
    this.pusher.unsubscribe(channel);
  }

  private releaseChannel(channel: string): void {
    const entry = this.channels.get(channel);
    if (!entry) return;
    entry.refCount -= 1;
    if (entry.refCount > 0) return;
    entry.graceTimer = setTimeout(() => this.dropChannel(channel), UNSUBSCRIBE_GRACE_MS);
  }

  private dropChannel(channel: string): void {
    const entry = this.channels.get(channel);
    if (!entry) return;
    if (entry.graceTimer !== null) clearTimeout(entry.graceTimer);
    if (entry.retryTimer !== null) clearTimeout(entry.retryTimer);
    this.channels.delete(channel);
    this.unsubscribeEntry(channel, entry);
  }

  private handleSubscriptionError(channel: string, data: unknown): void {
    const entry = this.channels.get(channel);
    if (!entry) return;
    this.unsubscribeEntry(channel, entry);
    entry.forbidden = isForbidden(data);
    if (entry.retried || entry.forbidden) return;
    entry.retried = true;
    entry.retryTimer = setTimeout(() => {
      entry.retryTimer = null;
      if (entry.refCount > 0) this.subscribeEntry(channel, entry);
    }, SUBSCRIPTION_RETRY_DELAY_MS);
  }

  ingest(batch: ChangeBatch): void {
    const fresh = batch.changes.filter((change) => this.markSeen(change.change_id));
    if (fresh.length === 0) return;
    recordRecentChanges(fresh);
    this.enqueueWhere((watches) => fresh.some((change) => this.matchesAny(change, watches)));
  }

  private handleSubscriptionSucceeded(channel: string): void {
    const entry = this.channels.get(channel);
    if (entry) entry.retried = false;
    if (this.reconnectTimer !== null) return;
    const subscribedAt = Date.now();
    this.enqueueWhere(
      (watches, query) =>
        (query.state.dataUpdatedAt < subscribedAt || query.state.fetchStatus === 'fetching') &&
        watches.some((watch) => changeChannel(watch.anchor) === channel),
    );
  }

  private matchesAny(change: EntityChange, watches: Watch[]): boolean {
    return watches.some((watch) => matches(change, watch));
  }

  private markSeen(changeId: string): boolean {
    if (this.seenChangeIds.has(changeId)) return false;
    this.seenChangeIds.add(changeId);
    if (this.seenChangeIds.size > DEDUPE_CAPACITY) {
      const oldest = this.seenChangeIds.values().next().value;
      if (oldest !== undefined) this.seenChangeIds.delete(oldest);
    }
    return true;
  }

  private enqueueWhere(predicate: (watches: Watch[], query: Query) => boolean): void {
    let added = false;
    this.queryClient
      .getQueryCache()
      .getAll()
      .forEach((query) => {
        const watches = watchesOf(query);
        if (watches.length > 0 && predicate(watches, query)) {
          this.queued.add(query.queryHash);
          added = true;
        }
      });
    if (added) this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.trailingTimer !== null) clearTimeout(this.trailingTimer);
    this.trailingTimer = setTimeout(this.flush, FLUSH_TRAILING_MS);
    if (this.maxWaitTimer === null) {
      this.maxWaitTimer = setTimeout(this.flush, FLUSH_MAX_WAIT_MS);
    }
  }

  private flush = (): void => {
    if (this.trailingTimer !== null) clearTimeout(this.trailingTimer);
    if (this.maxWaitTimer !== null) clearTimeout(this.maxWaitTimer);
    this.trailingTimer = null;
    this.maxWaitTimer = null;
    this.invalidateQueued();
  };

  private flushHeld(): void {
    if (this.trailingTimer === null) this.invalidateQueued();
  }

  private scheduleReconnectRefresh(): void {
    if (this.reconnectTimer !== null) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.enqueueWhere(() => true);
      this.flush();
    }, Math.random() * RECONNECT_JITTER_MAX_MS);
  }

  private heldAnchors(): Set<string> {
    const held = new Set(this.holds.keys());
    this.queryClient
      .getMutationCache()
      .getAll()
      .forEach((mutation) => {
        if (mutation.state.status !== 'pending') return;
        (mutation.meta?.affects ?? []).forEach((anchor) => held.add(changeChannel(anchor)));
      });
    return held;
  }

  private invalidateQueued(): void {
    if (this.queued.size === 0) return;
    const held = this.heldAnchors();
    const queryCache = this.queryClient.getQueryCache();
    [...this.queued].forEach((queryHash) => {
      const query = queryCache.get(queryHash);
      if (!query) {
        this.queued.delete(queryHash);
        return;
      }
      if (watchesOf(query).some((watch) => held.has(changeChannel(watch.anchor)))) return;
      this.queued.delete(queryHash);
      void this.queryClient.invalidateQueries({ queryKey: query.queryKey, exact: true });
    });
  }
}
